import { forwardRef, Inject, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CUSTOM_FILTERS_AND_COMPONENTS_MAP } from '../../common/setup';
import { ActionStatePipe } from './action-state.pipe';
import { AddOnLabelPipe } from './add-on-label.pipe';
import { BulkUpdateLocalizationPipe } from './bulk-update-localization.pipe';
import { ByteFormatterPipe } from './byte-formatter.pipe';
import { DataExportStatePipe } from './data-export-state.pipe';
import { DataImportStatePipe } from './data-import-state.pipe';
import { DefaultSimSessionStatusPipe } from './default-sim-session-status.pipe';
import { EventSeverityPipe } from './event-severity.pipe';
import { FirmwareUpdateStatusPipe } from './firmware-update-status.pipe';
import { AlertSeverityPipe, DatetimeFormatterPipe, DefaultCloudStatusPipe, DefaultConnectionStatusPipe, DefaultServiceLevelPipe } from './index';
import { PartnerStatusPipe } from './partner-status.pipe';
import { PlanLabelPipe } from './plan-label.pipe';
import { ServitlyBadgePipe } from './servitly-badge.pipe';
import { StatusTagPipe } from './status-tag.pipe';
import { UnderscoreRemoverPipe } from './underscore-remover.pipe';

@Pipe({
    name: 'loader'
})
export class LoaderPipe implements PipeTransform {

    private customFilters: any = CUSTOM_FILTERS_AND_COMPONENTS_MAP;

    constructor(
        @Inject(forwardRef(() => DomSanitizer)) private sanitizer: DomSanitizer,
        @Inject(forwardRef(() => DefaultServiceLevelPipe)) private defaultServiceLevelPipe: DefaultServiceLevelPipe,
        @Inject(forwardRef(() => UnderscoreRemoverPipe)) private underscoreRemoverPipe: UnderscoreRemoverPipe,
        @Inject(forwardRef(() => BulkUpdateLocalizationPipe)) private bulkUpdateLocalizePipe: BulkUpdateLocalizationPipe,
        @Inject(forwardRef(() => ByteFormatterPipe)) private byteFormatterPipe: ByteFormatterPipe,
        @Inject(forwardRef(() => DefaultSimSessionStatusPipe)) private defaultSimSessionStatusPipe: DefaultSimSessionStatusPipe,
        @Inject(forwardRef(() => AlertSeverityPipe)) private alertSeverityPipe: AlertSeverityPipe,
        @Inject(forwardRef(() => DefaultCloudStatusPipe)) private defaultCloudStatusPipe: DefaultCloudStatusPipe,
        @Inject(forwardRef(() => DefaultConnectionStatusPipe)) private defaultConnectionStatusPipe: DefaultConnectionStatusPipe,
        @Inject(forwardRef(() => FirmwareUpdateStatusPipe)) private firmwareUpdateStatusPipe: FirmwareUpdateStatusPipe,
        @Inject(forwardRef(() => ActionStatePipe)) private actionStatePipe: ActionStatePipe,
        @Inject(forwardRef(() => StatusTagPipe)) private statusTagPipe: StatusTagPipe,
        @Inject(forwardRef(() => DataExportStatePipe)) private dataImportStatePipe: DataImportStatePipe,
        @Inject(forwardRef(() => DataExportStatePipe)) private dataExportStatePipe: DataExportStatePipe,
        @Inject(forwardRef(() => DatetimeFormatterPipe)) private datetimeFormatterPipe: DatetimeFormatterPipe,
        @Inject(forwardRef(() => PlanLabelPipe)) private planLabelPipe: PlanLabelPipe,
        @Inject(forwardRef(() => AddOnLabelPipe)) private addOnLabelPipe: AddOnLabelPipe,
        @Inject(forwardRef(() => PartnerStatusPipe)) private partnerStatusPipe: PartnerStatusPipe,
        @Inject(forwardRef(() => ServitlyBadgePipe)) private syBadgePipe: ServitlyBadgePipe,
        @Inject(forwardRef(() => EventSeverityPipe)) private eventSeverityPipe: EventSeverityPipe
    ) {
    }

    transform(value: any, filterName: any, bypassSanitizeHtml?: boolean, pipeArgument?: any): any {
        let pipe = null;
        if (this.isChainedFilter(filterName)) {
            const filterNames = (filterName as string).split('|');
            for (let i = 0; i < filterNames.length; i++) {
                value = this.transform(value, filterNames[i], (i == (filterName.length - 1) ? bypassSanitizeHtml : true), pipeArgument);
            }
            return value;
        } else if (this.isFilterWithCustomArgs(filterName)) {
            const argPosition = filterName.indexOf(':');
            const customFilterName = filterName.substring(0, argPosition).trim();
            const argString = filterName.substring(argPosition + 1).trim();
            const updatedArgument = this.extractPipeArgument(argString, pipeArgument, customFilterName);
            return this.transform(value, customFilterName, bypassSanitizeHtml, updatedArgument);
        } else if (this.isCustomFilter(filterName)) {
            let className = this.customFilters[filterName];
            pipe = new className();
        } else if (this.isPrebuiltFilter(filterName)) {
            let pipeClass = filterName as any;
            pipe = new pipeClass();
            if (pipe instanceof DatetimeFormatterPipe) {
                pipe = this.datetimeFormatterPipe;
            } else if (pipe instanceof ServitlyBadgePipe) {
                pipe = this.syBadgePipe;
            } else if (pipe instanceof EventSeverityPipe) {
                pipe = this.eventSeverityPipe;
            } else if (pipe instanceof AlertSeverityPipe) {
                pipe = this.alertSeverityPipe;
            }
        } else if (filterName == 'defaultServiceLevel') {
            pipe = this.defaultServiceLevelPipe;
        } else if (filterName == 'underscoreRemover') {
            pipe = this.underscoreRemoverPipe;
        } else if (filterName == 'bulkUpdateLocalize') {
            pipe = this.bulkUpdateLocalizePipe;
        } else if (filterName == 'byteFormat') {
            pipe = this.byteFormatterPipe;
        } else if (filterName == 'defaultSimSessionStatus') {
            pipe = this.defaultSimSessionStatusPipe;
        } else if (filterName == 'alertSeverity') {
            pipe = this.alertSeverityPipe;
        } else if (filterName == 'defaultConnectionStatus') {
            pipe = this.defaultConnectionStatusPipe;
        } else if (filterName == 'defaultCloudStatus') {
            pipe = this.defaultCloudStatusPipe;
        } else if (filterName == 'firmwareUpdateStatus') {
            pipe = this.firmwareUpdateStatusPipe;
        } else if (filterName == 'actionState') {
            pipe = this.actionStatePipe;
        } else if (filterName == 'statusTag') {
            pipe = this.statusTagPipe;
        } else if (filterName == 'dataExportState') {
            pipe = this.dataExportStatePipe;
        } else if (filterName == 'dataImportState') {
            pipe = this.dataImportStatePipe;
        } else if (filterName == 'planLabel') {
            pipe = this.planLabelPipe;
        } else if (filterName == 'addOnLabel') {
            pipe = this.addOnLabelPipe;
        } else if (filterName == 'partnerStatus') {
            pipe = this.partnerStatusPipe;
        } else if (filterName == 'syBadge') {
            pipe = this.syBadgePipe;
        }
        if (pipe) {
            return this.transformAndSanitize(pipe, value, bypassSanitizeHtml, pipeArgument);
        }
        return value;
    }

    private isChainedFilter(filterName: any): boolean {
        return (typeof filterName == 'string') && filterName.includes('|');
    }

    private isFilterWithCustomArgs(filterName: any): boolean {
        return (typeof filterName == 'string') && filterName.includes(':');
    }

    private extractPipeArgument(argString: any, pipeArgument: any, customFilterName: string): any {
        try {
            const args = JSON.parse(argString?.replaceAll("'", '"'));
            if (customFilterName == 'syBadge') {
                return args;
            } else {
                return Object.assign({ parameters: args }, pipeArgument);
            }
        } catch {
            return pipeArgument;
        }
    }

    isCustomFilter(filterName: any): boolean {
        return filterName && this.customFilters && this.customFilters[filterName];
    }

    private isPrebuiltFilter(filterName: any): boolean {
        return filterName && typeof filterName !== 'string';
    }

    private transformAndSanitize(pipe: any, value: any, bypassSanitizeHtml?: boolean, pipeArgument?: any): any {
        let transformationResult = this.transformWithArgument(pipe, value, pipeArgument);
        if (bypassSanitizeHtml) {
            return transformationResult;
        }
        return this.sanitize(transformationResult);
    }

    private transformWithArgument(pipe: any, value: any, pipeArgument: any = null): any {
        if (pipeArgument == null) {
            return pipe.transform(value);
        } else {
            return pipe.transform(value, pipeArgument);
        }
    }

    private sanitize(value: any): any {
        return this.sanitizer.bypassSecurityTrustHtml(value);
    }
}